/** ClaimTradeUnion — the /claim/:slug flow.
 *
 *  Ghost trade unions (curated by the CALL·SHEET team from public
 *  sources) sit at claim_status=unclaimed. When an authorised office-
 *  bearer wants to activate the profile they walk through this wizard:
 *
 *    Step 1 · Identity       — name · role · email · phone
 *              → POST /trade-unions/{slug}/claim/initiate
 *              (backend mints a 6-digit OTP; in non-prod envs it comes
 *               back as `dev_otp` so demos work end-to-end)
 *    Step 2 · Verify OTP     — 6-digit code + set password
 *              → POST /trade-unions/{slug}/claim/verify
 *              (backend flips claim_status → claim_pending_kyc, creates
 *               the trade-org admin user, returns a JWT)
 *    Step 3 · KYC upload     — registration_number · certificate · bylaws · logo
 *              → POST /orgs/{org_id}/verify
 *              (backend flips claim_status → active AND verified=true —
 *               the trade union dashboard is now unlocked)
 *    Step 4 · Done           — CTA into the freshly-unlocked dashboard.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { setToken } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ShieldCheck, MailCheck, FileText, Sparkles, ArrowRight, ArrowLeft,
  Loader2, Award, Upload, CheckCircle2, KeyRound,
} from "lucide-react";

const STEPS = ["Identity", "Verify OTP", "KYC Docs", "Done"];

export default function ClaimTradeUnion() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [union, setUnion] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [step, setStep] = useState(0);

  // Step 1 state
  const [claimant, setClaimant] = useState({
    name: "", role: "", email: "", phone: "",
  });
  const [initiating, setInitiating] = useState(false);
  const [otpHint, setOtpHint] = useState("");   // masked email hint from backend
  const [devOtp, setDevOtp]   = useState("");   // dev-only auto-fill

  // Step 2 state
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [orgId, setOrgId] = useState("");

  // Step 3 state
  const [regNumber, setRegNumber] = useState("");
  const [certificate, setCertificate] = useState(null);
  const [bylaws, setBylaws] = useState(null);
  const [logo, setLogo] = useState(null);
  const [submittingKyc, setSubmittingKyc] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/trade-unions/${slug}`);
        setUnion(data);
        if (data.claim_status === "active") {
          setLoadErr("This union has already been claimed and activated.");
        }
      } catch (err) {
        setLoadErr(
          err?.response?.status === 404
            ? `No trade union found with slug "${slug}".`
            : "Could not load this trade union. Try again later."
        );
      }
    })();
  }, [slug]);

  // ─── Step 1 — initiate claim ───────────────────────────────────
  const submitIdentity = async () => {
    setInitiating(true);
    try {
      const { data } = await api.post(`/trade-unions/${slug}/claim/initiate`, {
        claimant_name:  claimant.name.trim(),
        claimant_role:  claimant.role.trim(),
        claimant_email: claimant.email.trim(),
        claimant_phone: claimant.phone.trim() || null,
      });
      setOtpHint(data.target_email_hint || "");
      setDevOtp(data.dev_otp || "");                // demo-mode auto-fill
      if (data.dev_otp) setOtp(data.dev_otp);
      toast.success("OTP dispatched. Check the union's official inbox.");
      setStep(1);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
        "Could not initiate claim."
      );
    } finally {
      setInitiating(false);
    }
  };

  // ─── Step 2 — verify OTP + set password ────────────────────────
  const submitOtp = async () => {
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== password2) {
      toast.error("Passwords don't match.");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.post(`/trade-unions/${slug}/claim/verify`, {
        otp: otp.trim(), password,
      });
      // Persist the JWT — the KYC endpoint requires auth.
      setToken(data.access_token);
      await refresh?.();
      setOrgId(data.organization_id);
      toast.success("Claim verified. One last step — KYC upload.");
      setStep(2);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
        "OTP verification failed."
      );
    } finally {
      setVerifying(false);
    }
  };

  // ─── Step 3 — upload KYC documents ─────────────────────────────
  const submitKyc = async () => {
    if (!certificate || !bylaws || !logo || regNumber.trim().length < 3) {
      toast.error("Registration number and all three files are required.");
      return;
    }
    setSubmittingKyc(true);
    try {
      const fd = new FormData();
      fd.append("registration_number", regNumber.trim());
      fd.append("certificate", certificate);
      fd.append("bylaws", bylaws);
      fd.append("logo", logo);
      await api.post(`/orgs/${orgId}/verify`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("KYC accepted. Your union is now live on Call·Sheet!");
      setStep(3);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
        "KYC upload failed."
      );
    } finally {
      setSubmittingKyc(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────
  if (loadErr) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16" data-testid="claim-load-error">
        <div className="cs-card rounded-2xl p-8 text-center">
          <Award size={32} className="mx-auto text-amber-300 mb-3" />
          <div className="font-display text-xl uppercase tracking-tight">Cannot start claim</div>
          <p className="mt-3 text-sm text-white/65">{loadErr}</p>
          <Link to="/directory/orgs?type=trade_org"
                className="mt-6 inline-block text-xs uppercase tracking-[0.22em] text-amber-300 hover:text-amber-200">
            ← Back to directory
          </Link>
        </div>
      </div>
    );
  }
  if (!union) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center text-white/55"
           data-testid="claim-loading">
        Loading claim wizard…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="claim-wizard">
      {/* Header */}
      <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/80 mb-2">
        Trade Union · Claim listing
      </div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight"
          data-testid="claim-wizard-heading">
        Claim {union.brand_name || union.legal_name}
      </h1>
      <p className="mt-3 text-sm text-white/60 max-w-xl leading-relaxed">
        Verify you&apos;re an authorised office-bearer to activate this
        listing. Takes about 3 minutes.
      </p>

      {/* Stepper */}
      <div className="mt-8 flex items-center gap-3 flex-wrap"
           data-testid="claim-stepper">
        {STEPS.map((label, i) => {
          const active = i === step;
          const done   = i < step;
          return (
            <div key={label}
                 className="flex items-center gap-3"
                 data-testid={`claim-step-${i}`}>
              <div className={
                "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold " +
                (done  ? "bg-emerald-500 text-black" :
                 active ? "bg-amber-400 text-black" :
                          "bg-white/10 text-white/50")
              }>
                {done ? <CheckCircle2 size={16}/> : i + 1}
              </div>
              <div className={
                "text-[11px] tracking-[0.22em] uppercase " +
                (active ? "text-white" : done ? "text-white/70" : "text-white/40")
              }>
                {label}
              </div>
              {i < STEPS.length - 1 && (
                <div className="w-6 h-px bg-white/15 mx-1" />
              )}
            </div>
          );
        })}
      </div>

      {/* Panel */}
      <div className="mt-8 cs-card rounded-2xl p-6 sm:p-8"
           data-testid={`claim-panel-step-${step}`}>
        {step === 0 && (
          <IdentityStep
            union={union}
            claimant={claimant} setClaimant={setClaimant}
            submitting={initiating} onSubmit={submitIdentity}
          />
        )}
        {step === 1 && (
          <OtpStep
            union={union} otpHint={otpHint} devOtp={devOtp}
            otp={otp} setOtp={setOtp}
            password={password} setPassword={setPassword}
            password2={password2} setPassword2={setPassword2}
            submitting={verifying} onSubmit={submitOtp}
            onBack={() => setStep(0)}
          />
        )}
        {step === 2 && (
          <KycStep
            regNumber={regNumber} setRegNumber={setRegNumber}
            certificate={certificate} setCertificate={setCertificate}
            bylaws={bylaws} setBylaws={setBylaws}
            logo={logo} setLogo={setLogo}
            submitting={submittingKyc} onSubmit={submitKyc}
          />
        )}
        {step === 3 && (
          <DoneStep slug={slug} onOpen={() => nav(`/trade-union-dashboard/${slug}`)} />
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* Step 1 · Identity                                                */
/* ────────────────────────────────────────────────────────────── */
function IdentityStep({ union, claimant, setClaimant, submitting, onSubmit }) {
  const set = (k) => (e) => setClaimant((c) => ({ ...c, [k]: e.target.value }));
  const canSubmit = claimant.name.trim().length > 1 &&
                    claimant.role.trim().length > 1 &&
                    /@/.test(claimant.email);
  return (
    <div data-testid="claim-identity-step">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-500/15 text-amber-300 flex items-center justify-center">
          <ShieldCheck size={20} />
        </div>
        <div>
          <div className="font-display text-lg uppercase tracking-tight">Your identity</div>
          <div className="text-[11px] text-white/55">
            Only office-bearers should proceed. All claims are audited.
          </div>
        </div>
      </div>

      {union.contact_email && (
        <div className="mt-6 rounded-lg border border-white/10 bg-black/30 p-3 text-[12px] text-white/70">
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/40">
            Where OTP will be sent
          </div>
          <div className="mt-1 font-mono text-white/85">{maskEmail(union.contact_email)}</div>
          <div className="mt-1 text-[11px] text-white/45">
            (This is the union&apos;s public contact email — you&apos;ll need access to that inbox.)
          </div>
        </div>
      )}

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field label="Full Name *">
          <Input value={claimant.name} onChange={set("name")}
                 placeholder="e.g. Rakesh Sharma"
                 className="bg-black/40 border-white/15"
                 data-testid="claim-name" />
        </Field>
        <Field label="Your Role in Union *">
          <Input value={claimant.role} onChange={set("role")}
                 placeholder="e.g. General Secretary"
                 className="bg-black/40 border-white/15"
                 data-testid="claim-role" />
        </Field>
        <Field label="Your Email *">
          <Input value={claimant.email} onChange={set("email")}
                 placeholder="you@union.org"
                 type="email"
                 className="bg-black/40 border-white/15"
                 data-testid="claim-email" />
        </Field>
        <Field label="Phone (optional)">
          <Input value={claimant.phone} onChange={set("phone")}
                 placeholder="+91 …"
                 className="bg-black/40 border-white/15"
                 data-testid="claim-phone" />
        </Field>
      </div>

      <Button className="mt-6 w-full h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-50"
              disabled={!canSubmit || submitting}
              onClick={onSubmit}
              data-testid="claim-identity-submit">
        {submitting ? <><Loader2 size={14} className="mr-2 animate-spin"/> Sending OTP…</> : <>Send OTP <ArrowRight size={14} className="ml-2"/></>}
      </Button>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* Step 2 · OTP + Password                                         */
/* ────────────────────────────────────────────────────────────── */
function OtpStep({ union, otpHint, devOtp,
                   otp, setOtp, password, setPassword, password2, setPassword2,
                   submitting, onSubmit, onBack }) {
  return (
    <div data-testid="claim-otp-step">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-500/15 text-amber-300 flex items-center justify-center">
          <MailCheck size={20} />
        </div>
        <div>
          <div className="font-display text-lg uppercase tracking-tight">Verify OTP</div>
          <div className="text-[11px] text-white/55">
            {otpHint ? <>Sent to <span className="font-mono">{otpHint}</span></>
                     : <>Check the union&apos;s inbox for the 6-digit code.</>}
          </div>
        </div>
      </div>

      {devOtp && (
        <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12px] text-amber-200"
             data-testid="claim-dev-otp-hint">
          <span className="uppercase tracking-[0.2em] text-[10px] font-semibold mr-2">
            Demo mode
          </span>
          OTP has been auto-filled: <span className="font-mono">{devOtp}</span>
        </div>
      )}

      <div className="mt-6 space-y-4">
        <Field label="6-digit OTP *">
          <Input value={otp} onChange={(e) => setOtp(e.target.value)}
                 placeholder="000000" maxLength={6}
                 inputMode="numeric"
                 className="bg-black/40 border-white/15 font-mono text-lg tracking-[0.4em]"
                 data-testid="claim-otp-input" />
        </Field>
        <Field label="Set Password (min 8 chars) *">
          <Input value={password} onChange={(e) => setPassword(e.target.value)}
                 type="password"
                 className="bg-black/40 border-white/15"
                 data-testid="claim-password" />
        </Field>
        <Field label="Confirm Password *">
          <Input value={password2} onChange={(e) => setPassword2(e.target.value)}
                 type="password"
                 className="bg-black/40 border-white/15"
                 data-testid="claim-password2" />
        </Field>
      </div>

      <div className="mt-6 flex gap-3">
        <Button variant="ghost"
                className="h-11 rounded-full text-[11px] uppercase tracking-[0.22em] text-white/70 hover:bg-white/5"
                onClick={onBack}
                data-testid="claim-otp-back">
          <ArrowLeft size={14} className="mr-2"/> Back
        </Button>
        <Button className="flex-1 h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-50"
                disabled={otp.length < 4 || !password || submitting}
                onClick={onSubmit}
                data-testid="claim-otp-submit">
          {submitting ? <><Loader2 size={14} className="mr-2 animate-spin"/> Verifying…</> : <>Verify & Continue <ArrowRight size={14} className="ml-2"/></>}
        </Button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* Step 3 · KYC upload                                             */
/* ────────────────────────────────────────────────────────────── */
function KycStep({ regNumber, setRegNumber, certificate, setCertificate,
                    bylaws, setBylaws, logo, setLogo,
                    submitting, onSubmit }) {
  return (
    <div data-testid="claim-kyc-step">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-500/15 text-amber-300 flex items-center justify-center">
          <FileText size={20} />
        </div>
        <div>
          <div className="font-display text-lg uppercase tracking-tight">KYC documents</div>
          <div className="text-[11px] text-white/55">
            Under the Societies Registration Act — both the incorporation certificate AND by-laws are mandatory.
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <Field label="Registration Number *">
          <Input value={regNumber} onChange={(e) => setRegNumber(e.target.value)}
                 placeholder="e.g. BOM/34/1953"
                 className="bg-black/40 border-white/15"
                 data-testid="claim-reg-number" />
        </Field>
        <FileField label="Incorporation Certificate * (PDF · JPG · PNG)"
                   file={certificate} onChange={setCertificate}
                   accept=".pdf,.jpg,.jpeg,.png"
                   testid="claim-cert-file" />
        <FileField label="Society By-Laws / Rules & Regulations * (PDF · DOC)"
                   file={bylaws} onChange={setBylaws}
                   accept=".pdf,.doc,.docx"
                   testid="claim-bylaws-file" />
        <FileField label="Official Logo * (PNG · JPG · SVG)"
                   file={logo} onChange={setLogo}
                   accept=".png,.jpg,.jpeg,.svg"
                   testid="claim-logo-file" />
      </div>

      <Button className="mt-6 w-full h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-50"
              disabled={submitting || !certificate || !bylaws || !logo || regNumber.length < 3}
              onClick={onSubmit}
              data-testid="claim-kyc-submit">
        {submitting ? <><Loader2 size={14} className="mr-2 animate-spin"/> Submitting KYC…</> : <>Submit & Activate <Sparkles size={14} className="ml-2"/></>}
      </Button>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* Step 4 · Done                                                   */
/* ────────────────────────────────────────────────────────────── */
function DoneStep({ slug, onOpen }) {
  return (
    <div className="text-center py-4" data-testid="claim-done-step">
      <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center">
        <CheckCircle2 size={30} />
      </div>
      <div className="mt-5 font-display text-2xl uppercase tracking-tight">
        You&apos;re live.
      </div>
      <p className="mt-3 text-sm text-white/65 max-w-md mx-auto">
        The listing has been activated and verified. Your union dashboard
        is now unlocked — start onboarding members, publishing notices,
        and (soon) collecting fees.
      </p>
      <div className="mt-6 flex gap-3 justify-center flex-wrap">
        <Button className="h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-emerald-500 text-black hover:bg-emerald-400"
                onClick={onOpen}
                data-testid="claim-open-dashboard">
          Open Dashboard <ArrowRight size={14} className="ml-2"/>
        </Button>
        <Link to={`/trade-union/${slug}`}
              className="inline-flex items-center h-11 px-5 rounded-full text-[11px] uppercase tracking-[0.22em] border border-white/15 text-white/75 hover:bg-white/5"
              data-testid="claim-view-public">
          View public profile
        </Link>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* Bits                                                            */
/* ────────────────────────────────────────────────────────────── */
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function FileField({ label, file, onChange, accept, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</Label>
      <label className="mt-2 flex items-center gap-3 rounded-lg border border-dashed border-white/20 bg-black/30 px-4 py-3 cursor-pointer hover:border-white/40 transition"
             data-testid={`${testid}-label`}>
        <Upload size={16} className="text-white/50"/>
        <span className="text-sm text-white/75 truncate flex-1">
          {file ? file.name : "Tap to select file"}
        </span>
        <input type="file" accept={accept} className="hidden"
               onChange={(e) => onChange(e.target.files?.[0] || null)}
               data-testid={testid} />
      </label>
    </div>
  );
}

function maskEmail(email) {
  if (!email || !email.includes("@")) return "";
  const [local, domain] = email.split("@");
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}***${local.slice(-1)}@${domain}`;
}
